import type { Locale } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { showMobileFeedback } from "./app-feedback.js";

const REFRESH_THRESHOLD = 64;
const MAX_PULL = 96;

interface PullGesture {
  readonly startX: number;
  readonly startY: number;
  readonly scroller: HTMLElement;
  axis: "pending" | "vertical";
}

export function MobilePullToRefresh({
  locale,
  onRefresh,
}: {
  readonly locale: Locale;
  readonly onRefresh: () => Promise<void>;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const onRefreshRef = useRef(onRefresh);
  const gesture = useRef<PullGesture | null>(null);
  const offsetRef = useRef(0);
  const refreshingRef = useRef(false);
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [indicatorTop, setIndicatorTop] = useState(72);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    const reset = () => {
      gesture.current = null;
      offsetRef.current = 0;
      setOffset(0);
    };
    const start = (event: TouchEvent) => {
      if (refreshingRef.current || event.touches.length !== 1) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          ".mobile-workspace-drawer,.mobile-workspace-sheet,.mobile-workspace-actions,[data-mobile-drawer-handle],[data-pull-refresh-ignore]",
        )
      )
        return;
      const scroller =
        target.closest<HTMLElement>(".chams-page") ??
        document.querySelector<HTMLElement>(".chams-page.app-shell");
      if (!scroller || scroller.scrollTop > 0) return;
      const header = document.querySelector<HTMLElement>(".chams-page.app-shell .shell-header");
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      setIndicatorTop(
        Math.max(
          12,
          Math.min(viewportHeight - 56, (header?.getBoundingClientRect().bottom ?? 60) + 12),
        ),
      );
      gesture.current = {
        startX: event.touches[0]!.clientX,
        startY: event.touches[0]!.clientY,
        scroller,
        axis: "pending",
      };
    };
    const move = (event: TouchEvent) => {
      const active = gesture.current;
      if (!active || event.touches.length !== 1) return;
      if (active.scroller.scrollTop > 0) {
        reset();
        return;
      }
      const deltaX = event.touches[0]!.clientX - active.startX;
      const distance = event.touches[0]!.clientY - active.startY;
      if (active.axis === "pending") {
        if (Math.max(Math.abs(deltaX), Math.abs(distance)) < 8) return;
        if (Math.abs(deltaX) >= Math.abs(distance) || distance <= 0) {
          reset();
          return;
        }
        if (Math.abs(distance) <= Math.abs(deltaX) * 1.25) return;
        active.axis = "vertical";
      }
      if (distance <= 0) {
        offsetRef.current = 0;
        setOffset(0);
        return;
      }
      if (event.cancelable) event.preventDefault();
      const next = Math.min(MAX_PULL, distance * 0.48);
      offsetRef.current = next;
      setOffset(next);
    };
    const finish = () => {
      if (!gesture.current) return;
      const shouldRefresh =
        gesture.current.axis === "vertical" && offsetRef.current >= REFRESH_THRESHOLD;
      reset();
      if (!shouldRefresh || refreshingRef.current) return;
      refreshingRef.current = true;
      setRefreshing(true);
      setOffset(48);
      const started = performance.now();
      void onRefreshRef
        .current()
        .then(() => showMobileFeedback("content-refreshed"))
        .catch(() => undefined)
        .finally(() => {
          const remaining = Math.max(0, 480 - (performance.now() - started));
          window.setTimeout(() => {
            refreshingRef.current = false;
            setRefreshing(false);
            setOffset(0);
          }, remaining);
        });
    };

    window.addEventListener("touchstart", start, { passive: true, capture: true });
    window.addEventListener("touchmove", move, { passive: false, capture: true });
    window.addEventListener("touchend", finish, { passive: true, capture: true });
    window.addEventListener("touchcancel", reset, { passive: true, capture: true });
    return () => {
      window.removeEventListener("touchstart", start, true);
      window.removeEventListener("touchmove", move, true);
      window.removeEventListener("touchend", finish, true);
      window.removeEventListener("touchcancel", reset, true);
    };
  }, []);

  const visible = refreshing || offset > 4;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-hidden={!visible}
      className="pointer-events-none fixed start-1/2 z-[45] flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-white px-3.5 py-2 font-heading text-xs font-bold text-primary shadow-e2 transition-[opacity,transform] duration-200 rtl:translate-x-1/2"
      style={{
        insetBlockStart: `${indicatorTop}px`,
        opacity: visible ? 1 : 0,
        transform: `translate3d(${locale === "ar" ? "50%" : "-50%"}, ${Math.max(-18, offset - 48)}px, 0)`,
      }}
    >
      <RefreshCw
        aria-hidden="true"
        className={`size-4 ${refreshing ? "animate-spin" : ""}`}
        style={{ rotate: refreshing ? undefined : `${Math.min(270, offset * 3)}deg` }}
      />
      {t(refreshing ? "pwa.refreshing" : "pwa.pullToRefresh")}
    </div>
  );
}
